import { useEffect, useRef, useState } from "react";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import type { GeoJSON as LeafletGeoJSON } from "leaflet";
import { useT } from "../../i18n/hooks.ts";
import { approximateGeometryAreaKm2, SPAIN_BOUNDS } from "../../model/gisLayer.ts";
import { fetchMitecoBasins } from "../../model/mitecoLayers.ts";
import type { MitecoBasinProperties } from "../../model/mitecoLayers.ts";
import { BASEMAPS } from "../basemap.ts";
import { MITECO_BASINS_STYLE } from "../mapStyles.ts";
import { escapeHtml } from "../escapeHtml.ts";
import { TooltipVisibilityFix } from "../TooltipVisibilityFix.tsx";

const SELECTED_STYLE = { color: "#e0913f", weight: 3, fillOpacity: 0.25, fillColor: "#e0913f" };

/**
 * Single-basin picker for Step1SubBasins.tsx's "MITECO" tab — deliberately
 * NOT MitecoSelectableLayer.tsx (that one is built for the editor's
 * multi-select "Add from MITECO" mode: Shift/Ctrl modifiers, rectangle
 * select, an `alreadyAdded` set). Here a plain click always REPLACES
 * whatever was selected before, there's nothing to add to a running batch —
 * exactly one basin ever makes sense for this flow.
 */
export function MitecoBasinPickerMap({
  selected,
  onSelect,
}: {
  selected: GeoJSON.Feature | null;
  onSelect: (feature: GeoJSON.Feature) => void;
}) {
  const t = useT();
  const basemap = BASEMAPS[0];
  const [data, setData] = useState<GeoJSON.FeatureCollection | null>(null);
  const layerRef = useRef<LeafletGeoJSON | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMitecoBasins().then((fetched) => {
      if (!cancelled) setData(fetched);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCode = selected ? (selected.properties as MitecoBasinProperties).basinCode : null;

  // Same repaint-in-place technique as MitecoSelectableLayer.tsx's own
  // effect — updates the highlight without remounting the layer (and so
  // without losing its tooltips/click handlers).
  useEffect(() => {
    layerRef.current?.setStyle((feature) => {
      const props = feature?.properties as MitecoBasinProperties;
      return props.basinCode === selectedCode ? SELECTED_STYLE : MITECO_BASINS_STYLE;
    });
  }, [selectedCode]);

  if (!data) return null;

  return (
    <div className="miteco-picker-map-wrap">
      <div className="map small-map compact">
        <MapContainer bounds={SPAIN_BOUNDS} scrollWheelZoom>
          <TileLayer
            attribution={basemap.attribution}
            url={basemap.url}
            maxZoom={basemap.maxZoom}
          />
          <GeoJSON
            ref={layerRef}
            data={data}
            style={MITECO_BASINS_STYLE}
            onEachFeature={(feature, layer) => {
              const props = feature.properties as MitecoBasinProperties;
              const areaKm2 = approximateGeometryAreaKm2(feature.geometry);
              layer.bindTooltip(
                `<strong>${escapeHtml(props.name)}</strong><br/>${escapeHtml(t("map.area", { km2: areaKm2.toFixed(1) }))}`,
                { sticky: true, direction: "top" }
              );
              layer.on("click", () => onSelect(feature));
            }}
          />
          <TooltipVisibilityFix />
        </MapContainer>
      </div>
    </div>
  );
}
